import { computed, reactive, toRefs } from "vue";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile
} from "firebase/auth";
import { auth } from "@/firebase";
import { ensureUserProfileDocument } from "@/services/userData";
import { logDebugEvent } from "@/services/debugLogs";

const state = reactive({
  user: null,
  profile: null,
  initialized: false,
  loading: false,
  error: null
});

let observerStarted = false;
let resolveReady;
const authReadyPromise = new Promise(resolve => {
  resolveReady = resolve;
});

function mapAuthError(error) {
  const code = error?.code || "";
  if (code === "auth/invalid-email") return "Please enter a valid email address.";
  if (code === "auth/missing-password") return "Please enter your password.";
  if (code === "auth/weak-password")
    return "Use a stronger password with at least 6 characters.";
  if (code === "auth/email-already-in-use")
    return "That email is already in use. Try logging in instead.";
  if (code === "auth/too-many-requests")
    return "Too many attempts right now. Please wait and try again.";
  if (code === "auth/user-not-found") return "No account found for that email.";
  if (code === "auth/wrong-password" || code === "auth/invalid-credential")
    return "The email or password is incorrect.";
  return "Something went wrong. Please try again.";
}

async function syncUserProfile(user) {
  if (!user) {
    state.profile = null;
    return null;
  }

  try {
    const profile = await ensureUserProfileDocument(user);
    state.profile = profile;
    return profile;
  } catch (error) {
    // Keep auth usable even when profile sync fails.
    console.error("Failed to sync user profile", error);
    return null;
  }
}

export async function refreshUserProfile() {
  if (!state.user) {
    state.profile = null;
    return null;
  }

  return syncUserProfile(state.user);
}

export function initializeAuthObserver() {
  if (observerStarted) return authReadyPromise;

  observerStarted = true;
  onAuthStateChanged(auth, async user => {
    state.user = user;
    await syncUserProfile(user);
    state.initialized = true;

    logDebugEvent("auth", "auth-state-changed", {
      isAuthenticated: !!user,
      uid: user?.uid || null,
      email: user?.email || null
    });

    resolveReady();
  });

  return authReadyPromise;
}

export async function waitForAuthReady() {
  initializeAuthObserver();
  await authReadyPromise;
}

export async function signUpUser(email, password, extraFields = {}) {
  state.loading = true;
  state.error = null;

  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);

    if (extraFields.displayName) {
      await updateProfile(credential.user, {
        displayName: extraFields.displayName
      });
    }

    await syncUserProfile(credential.user);
    return credential.user;
  } catch (error) {
    state.error = mapAuthError(error);
    throw new Error(state.error);
  } finally {
    state.loading = false;
  }
}

export async function logInUser(email, password) {
  state.loading = true;
  state.error = null;

  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await syncUserProfile(credential.user);

    logDebugEvent("auth", "login-success", {
      uid: credential.user?.uid || null,
      email: credential.user?.email || null
    });

    return credential.user;
  } catch (error) {
    state.error = mapAuthError(error);
    logDebugEvent("auth", "login-failed", {
      code: error?.code || null,
      message: error?.message || null
    });
    throw new Error(state.error);
  } finally {
    state.loading = false;
  }
}

export async function logOutUser() {
  state.loading = true;
  state.error = null;

  try {
    await signOut(auth);
    state.profile = null;
  } catch (error) {
    state.error = mapAuthError(error);
    throw new Error(state.error);
  } finally {
    state.loading = false;
  }
}

export async function sendResetPassword(email) {
  state.loading = true;
  state.error = null;

  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    state.error = mapAuthError(error);
    throw new Error(state.error);
  } finally {
    state.loading = false;
  }
}

export function useAuthState() {
  return {
    ...toRefs(state),
    isAuthenticated: computed(() => !!state.user)
  };
}
